/**
 * v7.18 — Admin Auth helper para o frontend.
 *
 * Fluxo:
 *  - getAdminToken() devolve o JWT em cache (sessionStorage) ou null.
 *  - setAdminToken(token, expiresAt) salva e agenda expiração.
 *  - clearAdminToken() apaga.
 *  - requestAdminPin() emite um CustomEvent que o `AdminPinDialog` global
 *    captura e exibe o modal. Devolve uma Promise que resolve com o PIN
 *    informado ou rejeita se o usuário cancelar.
 *  - adminLogin(pin) chama POST /api/auth/admin-login e atualiza o cache.
 *  - withAdminAuth(fetchFn) wrapper que injeta Authorization e refaz a
 *    chamada após relogar se 401 voltar.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORE_KEY = 'kaven_admin_token_v1';

function _now() { return Math.floor(Date.now() / 1000); }

export function getAdminToken() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token || !parsed?.expAt) return null;
    // 30s de folga para evitar usar token quase-expirado.
    if (_now() + 30 >= parsed.expAt) return null;
    return parsed.token;
  } catch {
    return null;
  }
}

export function setAdminToken(token, expiresInSeconds) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({
      token,
      expAt: _now() + Number(expiresInSeconds || 1800),
      savedAt: Date.now(),
    }));
  } catch {}
}

export function clearAdminToken() {
  try { sessionStorage.removeItem(STORE_KEY); } catch {}
}

/**
 * Dispara um CustomEvent que o `AdminPinDialog` montado globalmente captura.
 * Resolve com o PIN ou rejeita se cancelar.
 */
export function requestAdminPin(message) {
  return new Promise((resolve, reject) => {
    const id = `pin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const onResolve = (ev) => {
      if (ev.detail?.id !== id) return;
      window.removeEventListener('kaven:admin-pin-result', onResolve);
      if (ev.detail?.pin) resolve(ev.detail.pin);
      else reject(new Error('Login admin cancelado'));
    };
    window.addEventListener('kaven:admin-pin-result', onResolve);
    window.dispatchEvent(new CustomEvent('kaven:admin-pin-required', {
      detail: { id, message: message || 'Digite o PIN admin para confirmar a alteração' },
    }));
  });
}

export async function adminLogin(pin) {
  if (!API_URL) throw new Error('REACT_APP_BACKEND_URL ausente');
  const r = await fetch(`${API_URL}/api/auth/admin-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pin: String(pin || '') }),
  });
  if (!r.ok) {
    let detail = 'PIN inválido';
    try {
      const j = await r.json();
      detail = j?.detail || detail;
    } catch {}
    throw new Error(detail);
  }
  const body = await r.json();
  setAdminToken(body.token, body.expiresIn);
  return body;
}

/**
 * Garante que existe um token admin válido. Se não houver, prompts PIN.
 * Devolve a string do token. Lança se o usuário cancelar.
 */
export async function ensureAdminToken({ message } = {}) {
  const cached = getAdminToken();
  if (cached) return cached;
  const pin = await requestAdminPin(message);
  await adminLogin(pin);
  const fresh = getAdminToken();
  if (!fresh) throw new Error('Não foi possível obter token admin');
  return fresh;
}

/**
 * Wrapper de fetch que injeta Authorization. Em 401 retentar uma vez
 * promovendo prompt de PIN.
 */
export async function authedFetch(url, opts = {}, { message } = {}) {
  let token = await ensureAdminToken({ message });
  const doFetch = (tk) => fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      'Authorization': `Bearer ${tk}`,
    },
  });
  let resp = await doFetch(token);
  if (resp.status === 401) {
    // Token expirou ou foi revogado — força novo prompt.
    clearAdminToken();
    token = await ensureAdminToken({ message: 'Sessão admin expirou. Digite o PIN novamente.' });
    resp = await doFetch(token);
  }
  return resp;
}
